<template>
  <div
    class="recipient-form"
    :class="{ [$class('loading')]: loading }"
  >
    <checkbox-input
      :id="`gift-card-recipient-show-${sectionId}`"
      v-model="open"
      class="recipient-form__checkbox"
      :disabled="loading"
      :label="$string('product.form.send_as_gift')"
      :name="controlFlag"
    />

    <div
      v-if="open"
      class="recipient-form__container form-group"
    >
      <div class="form-group__field">
        <label
          class="form-group__label"
          :for="`gift-card-recipient-email-${sectionId}`"
        >
          <span v-text="$string('product.recipient_form.email.label')" />

          <abbr
            :title="$string('product.recipient_form.required.label')"
            v-text="$string('product.recipient_form.required.symbol')"
          />
        </label>

        <input
          :id="`gift-card-recipient-email-${sectionId}`"
          v-model="fields['Recipient email']"
          class="form-group__input"
          name="properties[Recipient email]"
          :placeholder="$string('product.recipient_form.email.placeholder')"
          required
          type="email"
        >
      </div>

      <div class="form-group__field">
        <label
          class="form-group__label text-body-s-regular"
          :for="`gift-card-recipient-name-${sectionId}`"
        >
          <span v-text="$string('product.recipient_form.name.label')" />
        </label>

        <input
          :id="`gift-card-recipient-name-${sectionId}`"
          v-model="fields['Recipient name']"
          class="form-group__input"
          name="properties[Recipient name]"
          :placeholder="$string('product.recipient_form.name.placeholder')"
          type="text"
        >
      </div>

      <div class="form-group__field">
        <label
          class="form-group__label text-body-s-regular"
          :for="`gift-card-recipient-message-${sectionId}`"
        >
          <span v-text="$string('product.recipient_form.message.label')" />
        </label>

        <textarea
          :id="`gift-card-recipient-message-${sectionId}`"
          v-model="fields.Message"
          class="form-group__input"
          :maxlength="messageLimit"
          name="properties[Message]"
          :placeholder="$string('product.recipient_form.message.placeholder')"
          type="text"
        />

        <span
          class="form-group__footer text-body-s-regular"
          v-text="$string(
            'product.recipient_form.message.limit',
            {
              pluralise: messageRemaining,
              replace: { remaining: messageRemaining },
            },
          )"
        />
      </div>

      <div class="form-group__field">
        <label
          class="form-group__label text-body-s-regular"
          :for="`gift-card-recipient-send-on-${sectionId}`"
        >
          <span v-text="$string('product.recipient_form.send_on.label')" />
        </label>

        <input
          :id="`gift-card-recipient-send-on-${sectionId}`"
          v-model="fields['Send on']"
          class="form-group__input"
          autocomplete="send_on"
          name="properties[Send on]"
          pattern="\d{4}-\d{2}-\d{2}"
          :placeholder="$string('product.recipient_form.send_on.placeholder')"
          type="date"
        >
      </div>
    </div>

    <input
      :id="`gift-card-recipient-control-${sectionId}`"
      disabled
      :name="controlFlag"
      type="hidden"
      :value="formActive ? 'on' : null"
    >

    <input
      :id="`gift-card-recipient-timezone-offset-${sectionId}`"
      disabled
      name="properties[__shopify_offset]"
      type="hidden"
      :value="timezoneOffset"
    >
  </div>
</template>

<script>

/**
 * Vue: Recipient form (product-form)
 * -----------------------------------------------------------------------------
 * Recipient form for gift card products.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {String} sectionId - Section ID.
 *
 */
import CheckboxInput from '~async/utils/checkbox-input/checkbox-input'

export default {
  name: 'RecipientForm',

  components: {
    CheckboxInput,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    sectionId: {
      type: String,
      default: '',
      required: true,
    },
  },

  emits: {
    'update-line-item-properties': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      controlFlag: 'properties[__shopify_send_gift_card_to_recipient]',
      fields: {
        Message: '',
        'Recipient email': '',
        'Recipient name': '',
        'Send on': '',
      },
      messageLimit: 200,
      open: false,
      timezoneOffset: '',
    }
  },

  computed: {

    /**
     * Compute if the form is active.
     * - Form is open and minimum field has been filled.
     * @returns {Boolean}
     */
    formActive() {
      return this.open && this.fields['Recipient email'] !== ''
    },

    /**
     * Compute gift card line item properties.
     * @returns {Object}
     */
    lineItemProperties() {
      let lineItemProperties = {
        __shopify_send_gift_card_to_recipient: null,
        __shopify_offset: null,
      }

      if (this.formActive) {
        lineItemProperties = {
          __shopify_send_gift_card_to_recipient: 'on',
          __shopify_offset: this.timezoneOffset,
        }
      }

      Object.entries(this.fields).forEach(([key, value]) => {
        if (!value || !this.formActive) {
          lineItemProperties[key] = null
          return
        }

        lineItemProperties[key] = value
      })

      return lineItemProperties
    },

    /**
     * Compute characters remaining in message field.
     * @returns {Number}
     */
    messageRemaining() {
      return this.messageLimit - this.fields.Message.length
    },
  },

  watch: {

    /**
     * Watch line item properties to emit updates.
     */
    lineItemProperties: {
      handler() {
        this.$emit('update-line-item-properties', this.lineItemProperties)
      },
      deep: true,
    },
  },

  beforeMount() {
    this.timezoneOffset = new Date().getTimezoneOffset().toString()
  },

  methods: {

    /**
     * Reset recipient form object.
     */
    reset() {
      this.open = false

      this.fields = {
        Message: '',
        'Recipient email': '',
        'Recipient name': '',
        'Send on': '',
      }
    },
  },
}
</script>

<style lang="scss">
@import './recipient-form';
</style>
